import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Logger from "../../../services/Logger";
import AdminWordWheelPackService from "../pack/AdminWordWheelPackService";
import AdminWordWheelService from "../tpl/AdminWordWheelService";
import {
    SEASON_LEVEL_CATALOG_MIN,
    WORD_WHEEL_DEFAULT_SEASON,
    WORD_WHEEL_SEASON_NONE,
    buildSeasonPublishedGroupsByCharacters,
    buildSeasonPublishedLevelGroups,
    isJourneyPack,
    summarizeSeasonPuzzles,
} from "../wordWheelLevelUtils";

const FALLBACK_PACKS = [
    { code: "season_2026", name: "Season 2026" },
    { code: "classic", name: "Classic" },
    { code: "hard_quest", name: "Hard Quest" },
    { code: "master", name: "Master" },
];

function packOptions(items) {
    const source = Array.isArray(items) && items.length > 0 ? items : FALLBACK_PACKS;
    return source
        .filter((pack) => pack?.code)
        .map((pack) => ({
            code: String(pack.code),
            name: pack.name || pack.code,
            locked: Boolean(pack.locked) || String(pack.code).startsWith("season_"),
        }))
        .sort((left, right) => {
            if (left.locked !== right.locked) {
                return left.locked ? -1 : 1;
            }
            return String(left.name).localeCompare(String(right.name));
        });
}

const formatNumber = (value) => (value != null ? value.toLocaleString() : "—");

const formatStat = (value) => (value != null ? Number(value).toLocaleString() : "—");

export default function WordWheelSeasonPublishedSection({
    language,
    season = WORD_WHEEL_DEFAULT_SEASON,
    onSeasonChange,
    reloadKey = 0,
    onManageLevels,
}) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [puzzles, setPuzzles] = useState([]);
    const [packs, setPacks] = useState(FALLBACK_PACKS);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const rows = await AdminWordWheelService.fetchPublishedBySeason(season, language);
                if (!cancelled) {
                    setPuzzles(rows);
                }
            } catch (err) {
                Logger.error("Failed to load seasonal published stats:", err);
                if (!cancelled) {
                    setError("Failed to load seasonal published statistics.");
                    setPuzzles([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [language, reloadKey, season]);

    useEffect(() => {
        let cancelled = false;
        const loadPacks = async () => {
            try {
                const items = await AdminWordWheelPackService.list();
                if (!cancelled && Array.isArray(items) && items.length > 0) {
                    setPacks(packOptions(items));
                }
            } catch (err) {
                Logger.error("Failed to load word wheel packs for published stats:", err);
            }
        };
        loadPacks();
        return () => {
            cancelled = true;
        };
    }, []);

    const summary = useMemo(() => summarizeSeasonPuzzles(puzzles), [puzzles]);
    const characterGroups = useMemo(
        () => buildSeasonPublishedGroupsByCharacters(puzzles),
        [puzzles]
    );
    const levelGroups = useMemo(
        () => buildSeasonPublishedLevelGroups(puzzles),
        [puzzles]
    );

    const options = useMemo(() => packOptions(packs), [packs]);
    const selectedPack = options.find((pack) => pack.code === season);
    const packLabel = season === WORD_WHEEL_SEASON_NONE ? "No pack" : (selectedPack?.name || season);

    const characterTotals = useMemo(() => {
        const scoreMins = characterGroups.map((group) => group.scoreMin).filter((value) => value != null);
        const scoreMaxs = characterGroups.map((group) => group.scoreMax).filter((value) => value != null);
        return {
            puzzleCount: puzzles.length,
            withLevel: summary.withLevel,
            levelMin: summary.levelMin,
            levelMax: summary.levelMax,
            scoreMin: scoreMins.length ? Math.min(...scoreMins) : null,
            scoreMax: scoreMaxs.length ? Math.max(...scoreMaxs) : null,
        };
    }, [characterGroups, puzzles.length, summary]);

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap", mb: 2 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                            Pack — published
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel id="ww-stats-season-label">Packs</InputLabel>
                                <Select
                                    labelId="ww-stats-season-label"
                                    label="Packs"
                                    value={season}
                                    onChange={(e) => onSeasonChange?.(e.target.value)}
                                >
                                    <MenuItem value={WORD_WHEEL_SEASON_NONE}>No pack</MenuItem>
                                    {options.map((pack) => (
                                        <MenuItem key={pack.code} value={pack.code}>
                                            {pack.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="body2" color="text.secondary">
                                {language} · published puzzles grouped by characters, level, and score.
                            </Typography>
                        </Box>
                    </Box>
                    {onManageLevels && isJourneyPack(season) && (
                        <Button variant="outlined" size="small" onClick={onManageLevels}>
                            Season stats + levels
                        </Button>
                    )}
                </Box>

                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && puzzles.length === 0 && (
                    <Alert severity="info">
                        {season === WORD_WHEEL_SEASON_NONE
                            ? "No published puzzles with no pack yet."
                            : `No published puzzles in ${packLabel} yet.`}
                    </Alert>
                )}

                {!loading && !error && puzzles.length > 0 && (
                    <>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 1, mb: 2 }}>
                            <SummaryTile label="Published" value={formatNumber(summary.total)} />
                            <SummaryTile label="With level" value={formatNumber(summary.withLevel)} />
                            <SummaryTile label="Without level" value={formatNumber(summary.withoutLevel)} />
                            <SummaryTile
                                label="Level range"
                                value={
                                    summary.levelMin != null && summary.levelMax != null
                                        ? `${summary.levelMin}–${summary.levelMax}`
                                        : "—"
                                }
                            />
                            <SummaryTile label="Locked" value={formatNumber(summary.locked)} />
                        </Box>

                        {isJourneyPack(season) && !summary.catalogComplete && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {summary.total} of {SEASON_LEVEL_CATALOG_MIN} published puzzles in {packLabel}.
                                Assign season levels from <strong>Season stats + levels</strong> once the catalog is complete.
                            </Alert>
                        )}

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            By characters
                        </Typography>
                        <TableContainer sx={{ overflowX: "auto", mb: 3 }}>
                            <Table size="small" stickyHeader sx={{ minWidth: 760 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Characters</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Published</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>With level</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Level min</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Level max</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Level avg</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Score min</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Score max</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Score avg</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Share</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {characterGroups.map((group) => (
                                        <TableRow key={group.characterCount}>
                                            <TableCell sx={{ fontWeight: 600 }}>{group.characterCount}</TableCell>
                                            <TableCell align="right">{formatNumber(group.puzzleCount)}</TableCell>
                                            <TableCell align="right">{formatNumber(group.withLevel)}</TableCell>
                                            <TableCell align="right">{formatStat(group.levelMin)}</TableCell>
                                            <TableCell align="right">{formatStat(group.levelMax)}</TableCell>
                                            <TableCell align="right">{formatStat(group.levelAvg)}</TableCell>
                                            <TableCell align="right">{formatStat(group.scoreMin)}</TableCell>
                                            <TableCell align="right">{formatStat(group.scoreMax)}</TableCell>
                                            <TableCell align="right">{formatStat(group.scoreAvg)}</TableCell>
                                            <TableCell align="right">{group.puzzleSharePercent.toFixed(1)}%</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatNumber(characterTotals.puzzleCount)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatNumber(characterTotals.withLevel)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatStat(characterTotals.levelMin)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatStat(characterTotals.levelMax)}
                                        </TableCell>
                                        <TableCell align="right">—</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatStat(characterTotals.scoreMin)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatStat(characterTotals.scoreMax)}
                                        </TableCell>
                                        <TableCell align="right">—</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>100.0%</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            By level
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Published puzzles grouped into level bands of 100. Unassigned means no pack level yet.
                        </Typography>
                        <TableContainer sx={{ overflowX: "auto" }}>
                            <Table size="small" stickyHeader sx={{ minWidth: 680 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Level band</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Published</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Level min</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Level max</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Level avg</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Score min</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Score max</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Score avg</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {levelGroups.map((group) => (
                                        <TableRow key={group.levelBucket}>
                                            <TableCell sx={{ fontWeight: 600 }}>{group.levelBucket}</TableCell>
                                            <TableCell align="right">{formatNumber(group.puzzleCount)}</TableCell>
                                            <TableCell align="right">{formatStat(group.levelMin)}</TableCell>
                                            <TableCell align="right">{formatStat(group.levelMax)}</TableCell>
                                            <TableCell align="right">{formatStat(group.levelAvg)}</TableCell>
                                            <TableCell align="right">{formatStat(group.scoreMin)}</TableCell>
                                            <TableCell align="right">{formatStat(group.scoreMax)}</TableCell>
                                            <TableCell align="right">{formatStat(group.scoreAvg)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

function SummaryTile({ label, value }) {
    return (
        <Box sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {value}
            </Typography>
        </Box>
    );
}
